import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { TooltipProvider } from "./components/ui/tooltip.jsx";
import { AuthProvider } from "./context/AuthContext.jsx";
import { ClientProvider } from "./context/ClientContext.jsx";
import MantenimientoGate from "./components/MantenimientoGate.jsx";
import { CurrencyProvider } from "./context/CurrencyContext.jsx";
import { ToastProvider } from "./components/Toast.jsx";
import { ConfirmProvider } from "./components/ConfirmDialog.jsx";
import App from "./App.jsx";
import * as Sentry from "@sentry/react";
import { PaginaError } from "./components/sistema/PaginasSistema.jsx";
import { initSentry, ErrorBoundary } from "./lib/sentry.js";
import { isChunkLoadError } from "./lib/chunk-error.js";
import "./index.css";
import "./lib/icons.css";

// Después de «Actualizar ahora» la página se recarga con ?_v=…: se saca de la dirección apenas carga.
try { const u = new URL(window.location.href); if (u.searchParams.has("_v")) { u.searchParams.delete("_v"); window.history.replaceState(null, "", u.pathname + u.search + u.hash); } } catch { /* sin history */ }

// Inicializar Sentry ANTES de renderizar — captura errores en el bootstrap
initSentry();

/* ─── PWA · registrar Service Worker propio del OS ─────────────────────────
 * El OS tiene su SW (sw-os.js) con NetworkOnly para /api/* y NetworkFirst
 * para navegación. Auto-updates: si hay versión nueva, recargamos.
 */
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  import("virtual:pwa-register").then(({ registerSW }) => {
    const updateSW = registerSW({
      onNeedRefresh() {
        window.__swUpdate = updateSW;
        window.dispatchEvent(new CustomEvent("sw-update-available"));
      },
      onOfflineReady() {
        console.info("[PWA] OS listo para uso offline (shell)");
      },
      immediate: true,
    });
  }).catch(err => console.warn("[PWA] registro falló:", err.message));

  // Cuando el SW nuevo se activa, NO recargar automáticamente.
  // (jun 2026 v5.1.1) — el auto-reload causaba que la página "pestañeara" sin aviso
  // cada vez que deployábamos. Ahora el VersionBadge muestra un toast pidiendo
  // recargar cuando detecta que backend.version !== frontend.version. El usuario
  // decide cuándo recargar; la SW nueva se activará automáticamente en el próximo
  // refresh manual o navegación dura.
  // La página puede seguir corriendo el bundle viejo aunque el SW nuevo ya tomó control (skipWaiting).
  // Antes no había aviso: la versión de package.json no cambia entre builds, así que VersionBadge no
  // detecta nada. Si ya había un SW controlando esta página al cargar y llega SW_UPDATED, se muestra
  // el aviso "Nueva versión disponible" (no bloqueante; recargar queda a elección del usuario).
  const teniaControlador = !!navigator.serviceWorker?.controller;
  navigator.serviceWorker?.addEventListener("message", (event) => {
    if (event.data?.type === "SW_UPDATED") {
      console.info("[PWA] SW actualizado");
      if (teniaControlador) window.dispatchEvent(new CustomEvent("sw-update-available"));
    }
  });

  // Revisión de versión por la propia página: pide el index.html (sin caché) y compara el paquete principal con el que está corriendo.
  // En el iPhone la app instalada queda suspendida días y el service worker casi nunca avisa; esto corre al volver a la app y cada 5 minutos.
  // Si hay uno nuevo, muestra el mismo aviso «Llegó SOA 10 PRO · Actualizar ahora».
  const paqueteActual = (() => { try { return new URL(import.meta.url).pathname; } catch { return null; } })();
  let ultimoChequeo = 0, avisada = false;
  async function revisarVersion() {
    if (avisada || !paqueteActual || !/\/assets\/index-/.test(paqueteActual)) return;
    if (Date.now() - ultimoChequeo < 60 * 1000) return;
    ultimoChequeo = Date.now();
    try {
      const r = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store" });
      const m = (await r.text()).match(/\/assets\/index-[\w-]+\.js/);
      if (m && m[0] !== paqueteActual) { avisada = true; window.dispatchEvent(new CustomEvent("sw-update-available")); }
    } catch { /* sin red: se vuelve a intentar */ }
    navigator.serviceWorker?.getRegistration?.().then((reg) => reg?.update?.()).catch(() => {});
  }
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") revisarVersion(); });
  window.addEventListener("focus", revisarVersion);
  window.addEventListener("pageshow", revisarVersion);
  setInterval(revisarVersion, 5 * 60 * 1000);
  setTimeout(revisarVersion, 15 * 1000);

  // Botón de emergencia: invocar window.__crewForceReload() desde la consola para
  // limpiar todo el cache del SW y recargar. Útil cuando el auto-reload no disparó.
  window.__crewForceReload = async () => {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
      console.info("[PWA] Caches limpiadas — recargando…");
    } finally {
      window.location.reload();
    }
  };
}

/* ─── Auto-recovery de chunks staled ──────────────────────────────────────
 * Cuando deployamos nuevo build, los chunks viejos (index-XXX.js) desaparecen.
 * Si el navegador tiene cacheado el manifest viejo y trata de cargar un chunk
 * que ya no existe, falla con "Failed to fetch dynamically imported module".
 * Detectamos esto y forzamos un reload limpio (1 vez por sesión).
 */
const RELOAD_FLAG = "__crew_chunk_reload";
function handleChunkError(err) {
  if (!isChunkLoadError(err)) return false;
  if (sessionStorage.getItem(RELOAD_FLAG)) return false; // ya reloadeamos esta sesión
  sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  console.warn("[chunk-reload] stale build detectado — recargando…");
  // Forzar bypass de service worker cache si existe
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then(regs => Promise.all(regs.map(r => r.unregister())))
      .finally(() => window.location.reload());
  } else {
    window.location.reload();
  }
  return true;
}
// Captura errors globales y promesas no manejadas
window.addEventListener("error", e => handleChunkError(e.error || e.message));
window.addEventListener("unhandledrejection", e => handleChunkError(e.reason));

function ErrorFallback({ error }) {
  // Si fue chunk-load error y aún no recargamos, intentar ahora
  if (typeof window !== "undefined" && handleChunkError(error)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted p-4">
        <div className="text-sm text-muted-foreground">Actualizando la app…</div>
      </div>
    );
  }
  const [showDetails, setShowDetails] = React.useState(false);
  // Intentamos extraer info útil aunque el error venga null/string/etc.
  let errorMsg = "Sin mensaje";
  let errorStack = "Sin stack";
  try {
    if (error == null) {
      errorMsg = "Error null/undefined — probablemente un import faltante o un componente que devolvió undefined";
    } else if (typeof error === "string") {
      errorMsg = error;
    } else if (error instanceof Error) {
      errorMsg = error.message || error.name || "Error sin mensaje";
      errorStack = error.stack || "Sin stack";
    } else {
      // Objeto, intentamos JSON
      try { errorMsg = JSON.stringify(error, null, 2); }
      catch { errorMsg = String(error); }
    }
  } catch (e) {
    errorMsg = "Error al formatear el error";
  }
  // Página de sistema «Algo no anduvo» (brand book §14). La referencia es el id del último evento de Sentry.
  let referencia = null;
  try { referencia = Sentry.lastEventId?.() || null; } catch { /* sin Sentry */ }
  return (
    <PaginaError
      referencia={referencia ? String(referencia).slice(0, 8) : null}
      detalle={`${errorMsg}

${errorStack}`}
      onReintentar={() => { try { sessionStorage.removeItem(RELOAD_FLAG); } catch {} window.location.reload(); }}
    />
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ErrorBoundary fallback={({ error }) => <ErrorFallback error={error} />} showDialog={false}>
      <BrowserRouter>
        <ToastProvider>
          <ConfirmProvider>
            <AuthProvider>
              <ClientProvider>
                <CurrencyProvider>
                  <TooltipProvider delayDuration={300}>
                    <App />
                    <MantenimientoGate />
                  </TooltipProvider>
                </CurrencyProvider>
              </ClientProvider>
            </AuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
